import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import cookie from "react-cookies";
import {
  getAcademyModule,
  getAcademyQuiz,
  submitAcademyQuiz,
} from "../../services/academyApi";
import { trackAcademyEvent, flushAcademyEvents } from "../../services/academyTracker";
import { AcademyModuleIcon } from "./AcademyIcons";
import AcademyLessonContent from "./AcademyLessonContent";
import { useLessonEngagement } from "./useLessonEngagement";
import { displayProgramTitle, TRAINING, buildAcademyBreadcrumbs } from "./trainingTerms";
import {
  getLessonProgress,
  OPTION_LETTERS,
  scrollTrainingToTop,
  TrainingFeedback,
  TrainingLessonList,
  TrainingLoading,
  TrainingProgressBar,
  TrainingStickyActions,
  TrainingSteps,
  TrainingTitleRow,
  CertBlockersList,
  lessonTypeLabel,
  formatLessonDuration,
  sumLessonDurations,
} from "./AcademyShared";

export default function AcademyModule({ Layout }) {
  const { academySlug, moduleSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [completing, setCompleting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [engagementPct, setEngagementPct] = useState(0);
  const videoRef = useRef(null);

  const minWatchPct = data?.module?.min_watch_pct ?? 85;
  const { flushProgress, getEngagementPct } = useLessonEngagement({
    lesson: activeLesson,
    moduleId: data?.module?.id,
    enabled: !!activeLesson && !showQuiz && !quizResult,
    videoRef,
  });

  useEffect(() => {
    if (!activeLesson) {
      setEngagementPct(0);
      return undefined;
    }
    const interval = setInterval(() => {
      setEngagementPct(getEngagementPct());
    }, 1000);
    return () => clearInterval(interval);
  }, [activeLesson, getEngagementPct]);

  const load = useCallback(async () => {
    if (!cookie.load("candidateKey")) {
      navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      return null;
    }
    const res = await getAcademyModule(moduleSlug, academySlug);
    setData(res);
    trackAcademyEvent("module_open", { module_id: res.module.id });
    return res;
  }, [moduleSlug, academySlug, navigate]);

  useEffect(() => {
    setActiveLesson(null);
    setShowQuiz(false);
    setQuizResult(null);
    setQuizQuestions([]);
    setQuizAnswers({});
    setQuestionIdx(0);
    setFeedback(null);
    load().catch(() => setData(null));
    scrollTrainingToTop({ instant: true });
    return () => flushAcademyEvents();
  }, [load]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 6000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const lessons = data?.lessons || [];
  const { pct, done, total } = getLessonProgress(lessons);
  const moduleDurationLabel = formatLessonDuration(sumLessonDurations(lessons));
  const moduleComplete = data?.progress?.status === "completed";
  const quizBestScore = data?.progress?.quiz_best_score ?? null;
  const quizCanSubmit = data?.quiz?.can_submit !== false;
  const quizAttemptsRemaining = data?.quiz?.attempts_remaining;
  const allQuizAnswered =
    quizQuestions.length > 0 && quizQuestions.every((qq) => quizAnswers[qq.id]);
  const steps = [...lessons.map((l) => l.title.slice(0, 12)), "Quiz"];
  const quizStepDone =
    (data?.progress?.quiz_best_score ?? 0) >= (data?.quiz?.pass_score_pct ?? 70);
  const stepsCompleted = [...lessons.map((l) => l.status === "completed"), quizStepDone];
  const currentStep = activeLesson
    ? lessons.findIndex((l) => l.id === activeLesson.id)
    : showQuiz
      ? lessons.length
      : -1;
  const nextLesson = lessons.find((l) => l.status !== "completed");

  const openLesson = (lesson, lessonList = lessons) => {
    const fresh = lessonList.find((l) => l.id === lesson.id) || lesson;
    setActiveLesson(fresh);
    setShowQuiz(false);
    setQuizResult(null);
    setFeedback(null);
    scrollTrainingToTop({ instant: true });
    trackAcademyEvent("lesson_start", {
      module_id: data.module.id,
      lesson_id: fresh.id,
    });
  };

  const completeLesson = async () => {
    if (!activeLesson || completing) return;
    const pct = getEngagementPct();
    if (pct < minWatchPct) {
      const action =
        activeLesson.lesson_type === "video"
          ? "Watch at least"
          : "Scroll through at least";
      setFeedback({
        type: "error",
        message: `${action} ${minWatchPct}% of this lesson before marking complete.`,
      });
      return;
    }
    setCompleting(true);
    try {
      await flushProgress({ lesson_complete: true, force: true });
      trackAcademyEvent("lesson_complete", {
        module_id: data.module.id,
        lesson_id: activeLesson.id,
        engagement_pct: getEngagementPct(),
      });
      await flushAcademyEvents();
      const refreshed = await load();
      const nextLessons = refreshed?.lessons || [];
      setFeedback({ type: "success", message: "Lesson complete." });
      const idx = nextLessons.findIndex((l) => l.id === activeLesson.id);
      const next = nextLessons[idx + 1];
      if (next) openLesson(next, nextLessons);
      else if (refreshed?.quiz) startQuiz();
      else setActiveLesson(null);
    } catch (e) {
      setFeedback({ type: "error", message: e.message });
    } finally {
      setCompleting(false);
    }
  };

  const startQuiz = async () => {
    if (!data?.quiz) return;
    if (data.quiz.can_submit === false) {
      setFeedback({
        type: "error",
        message: `You have used all ${data.quiz.max_attempts} quiz attempts. Review the lessons before trying again.`,
      });
      return;
    }
    setQuizLoading(true);
    setFeedback(null);
    try {
      const res = await getAcademyQuiz(data.quiz.id);
      if (res.quiz?.can_submit === false) {
        setFeedback({
          type: "error",
          message: `You have used all ${res.quiz.max_attempts} quiz attempts. Review the lessons before trying again.`,
        });
        return;
      }
      setData((prev) =>
        prev ? { ...prev, quiz: { ...prev.quiz, ...res.quiz } } : prev
      );
      setQuizQuestions(res.questions || []);
      setQuizAnswers({});
      setQuestionIdx(0);
      setQuizResult(null);
      setShowQuiz(true);
      setActiveLesson(null);
      scrollTrainingToTop({ instant: true });
    } catch (e) {
      setFeedback({ type: "error", message: e.message });
    } finally {
      setQuizLoading(false);
    }
  };

  const submitQuiz = async () => {
    if (!data?.quiz) return;
    if (!allQuizAnswered) {
      setFeedback({
        type: "error",
        message: "Answer every question before submitting.",
      });
      return;
    }
    if (data.quiz.can_submit === false) {
      setFeedback({
        type: "error",
        message: `You have used all ${data.quiz.max_attempts} quiz attempts.`,
      });
      return;
    }
    setCompleting(true);
    setFeedback(null);
    try {
      const res = await submitAcademyQuiz(data.quiz.id, quizAnswers);
      setQuizResult(res);
      setShowQuiz(false);
      if (res.passed) {
        const certMsg =
          res.new_certificates?.length > 0
            ? ` New certificate issued: ${res.new_certificates.map((c) => c.title).join(", ")}.`
            : res.cert_eligible_hint
              ? ` ${res.cert_eligible_hint}`
              : res.quiz_perfect
                ? ""
                : " Score 100% on the quiz to unlock certification.";
        setFeedback({
          type: "success",
          message: `Passed with ${res.score_pct}%. Module complete.${certMsg}`,
        });
        await load();
      }
    } catch (e) {
      setFeedback({ type: "error", message: e.message });
      await load();
    } finally {
      setCompleting(false);
    }
  };

  if (!data) {
    return (
      <Layout title="Module" onBack={() => navigate(`/academy/${academySlug}`)}>
        <TrainingLoading label="Loading module..." />
      </Layout>
    );
  }

  const backUrl = `/academy/${academySlug}`;
  const moduleBreadcrumbs = (currentLabel) =>
    buildAcademyBreadcrumbs({
      programTitle: displayProgramTitle(data?.module?.academy_title),
      programSlug: data?.module?.academy_slug || academySlug,
      moduleTitle: data?.module?.title,
      moduleSlug: data?.module?.slug,
      currentLabel,
    });

  if (!activeLesson && !showQuiz && !quizResult) {
    return (
      <Layout
        title={data.module.title}
        titleChip="module"
        onBack={() => navigate(backUrl)}
        breadcrumbs={moduleBreadcrumbs()}
      >
        <div className="training-hook">
          <div className="training-hook-icon">
            <AcademyModuleIcon name={data.module.icon} size={24} />
          </div>
          <div>
            <div className="training-kicker">{displayProgramTitle(data.module.academy_title)}</div>
            <TrainingTitleRow
              typeChip="module"
              title={data.module.title}
              className="training-title-row--module-hook"
            />
            {data.module.description && (
              <p className="training-subtitle" style={{ marginBottom: 0 }}>
                {data.module.description}
              </p>
            )}
          </div>
        </div>

        <TrainingProgressBar
          value={pct}
          label={`${done} of ${total} ${TRAINING.lessons.toLowerCase()} complete`}
        />

        <h3 className="training-section-title">
          {TRAINING.lessons}
          {moduleDurationLabel && (
            <span className="training-section-meta">About {moduleDurationLabel} total</span>
          )}
        </h3>
        <TrainingLessonList
          lessons={lessons}
          onSelect={openLesson}
          nextLessonId={nextLesson?.id}
        />

        {data.quiz && (
          <div className="training-card" style={{ marginTop: 16 }}>
            <h3 className="training-program-title">Final {TRAINING.quiz.toLowerCase()}</h3>
            {moduleComplete ? (
              <>
                <p className="training-subtitle" style={{ fontSize: 15, marginBottom: 12 }}>
                  Module complete. Best quiz score: {quizBestScore ?? "n/a"}%.
                </p>
                <CertBlockersList items={data.certification_blockers} />
                {quizBestScore != null && quizBestScore < 100 && quizCanSubmit && (
                  <button
                    type="button"
                    className="training-btn-primary"
                    onClick={startQuiz}
                    disabled={quizLoading}
                  >
                    {quizLoading ? "Loading..." : "Retake for 100%"}
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="training-subtitle" style={{ fontSize: 15, marginBottom: 12 }}>
                  {data.quiz.pass_score_pct >= 100
                    ? "Score 100% (every question correct) to complete this module."
                    : `Score ${data.quiz.pass_score_pct}% or higher to complete this ${TRAINING.module.toLowerCase()}.`}
                  {typeof quizAttemptsRemaining === "number" && (
                    <> {quizAttemptsRemaining} of {data.quiz.max_attempts} attempts remaining.</>
                  )}
                </p>
                <button
                  type="button"
                  className="training-btn-primary"
                  onClick={startQuiz}
                  disabled={
                    quizLoading ||
                    (lessons.length > 0 && done < lessons.length) ||
                    !quizCanSubmit
                  }
                >
                  {quizLoading
                    ? "Loading..."
                    : !quizCanSubmit
                      ? "No quiz attempts remaining"
                      : done < lessons.length
                        ? `Complete ${TRAINING.lessons.toLowerCase()} first`
                        : `Take ${TRAINING.quiz.toLowerCase()}`}
                </button>
              </>
            )}
          </div>
        )}

        <TrainingFeedback feedback={feedback} />
      </Layout>
    );
  }

  if (showQuiz && !quizResult) {
    const q = quizQuestions[questionIdx];
    return (
      <Layout title={TRAINING.quiz} onBack={() => setShowQuiz(false)} breadcrumbs={moduleBreadcrumbs(`Final ${TRAINING.quiz.toLowerCase()}`)}>
        <TrainingSteps steps={steps} current={lessons.length} completed={stepsCompleted} />
        {quizLoading || !q ? (
          <TrainingLoading label="Loading quiz..." />
        ) : (
          <>
            <div className="training-quiz-header">
              <div className="training-kicker">
                Question {questionIdx + 1} of {quizQuestions.length}
              </div>
              <h2 className="training-quiz-question">{q.question_text}</h2>
            </div>
            <div className="training-quiz-options training-quiz-options--grid">
              {(q.options || []).map((opt, i) => (
                <button
                  key={`${q.id}-${i}`}
                  type="button"
                  className={`training-quiz-option ${quizAnswers[q.id] === opt ? "selected" : ""}`}
                  onClick={() => setQuizAnswers({ ...quizAnswers, [q.id]: opt })}
                >
                  <span className="training-quiz-option-letter">{OPTION_LETTERS[i]}</span>
                  {opt}
                </button>
              ))}
            </div>
            <TrainingFeedback feedback={feedback} />
            <TrainingStickyActions>
              <div className="training-sticky-inner training-quiz-nav">
                {questionIdx > 0 && (
                  <button
                    type="button"
                    className="training-btn-secondary"
                    onClick={() => setQuestionIdx(questionIdx - 1)}
                  >
                    Back
                  </button>
                )}
                {questionIdx < quizQuestions.length - 1 ? (
                  <button
                    type="button"
                    className="training-btn-primary"
                    disabled={!quizAnswers[q.id]}
                    onClick={() => setQuestionIdx(questionIdx + 1)}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    className="training-btn-primary"
                    disabled={!allQuizAnswered || completing || !quizCanSubmit}
                    onClick={submitQuiz}
                  >
                    {completing
                      ? "Submitting..."
                      : !allQuizAnswered
                        ? "Answer all questions"
                        : "Submit quiz"}
                  </button>
                )}
              </div>
            </TrainingStickyActions>
          </>
        )}
      </Layout>
    );
  }

  if (quizResult) {
    return (
      <Layout title="Results" onBack={() => navigate(backUrl)} breadcrumbs={moduleBreadcrumbs("Quiz results")}>
        <div className="training-result-panel">
          <div className={`training-result-card ${quizResult.passed ? "training-result-card--pass" : "training-result-card--fail"}`}>
            <div className="training-kicker">{quizResult.passed ? "Passed" : "Try again"}</div>
            <div className="training-result-score">{quizResult.score_pct}%</div>
            <p className="training-subtitle" style={{ marginBottom: 0 }}>
              {quizResult.passed
                ? quizResult.quiz_perfect
                  ? `Perfect score. This module counts toward certification.`
                  : `Module complete at ${quizResult.score_pct}%. Retake for 100% to unlock certification.`
                : `You need ${data.quiz?.pass_score_pct || 70}% to pass. Review the ${TRAINING.lessons.toLowerCase()} and try again.`}
            </p>
          </div>
          {quizResult.new_certificates?.length > 0 && (
            <div className="training-cert-celebrate">
              <strong>Certificate issued</strong>
              <p>{quizResult.new_certificates.map((c) => c.title).join(", ")}</p>
              <Link to="/me/certificates" className="training-btn-primary" style={{ textDecoration: "none" }}>
                Download certificate
              </Link>
            </div>
          )}
          {quizResult.module_complete &&
            !quizResult.new_certificates?.length &&
            quizResult.certification_blockers?.length > 0 && (
            <CertBlockersList items={quizResult.certification_blockers} />
          )}
          <div className="training-result-actions">
            {!quizResult.passed && quizCanSubmit && (
              <button type="button" className="training-btn-secondary" onClick={startQuiz}>
                Retry quiz
              </button>
            )}
            {quizResult.passed && !quizResult.quiz_perfect && quizCanSubmit && (
              <button type="button" className="training-btn-secondary" onClick={startQuiz}>
                Retake for 100%
              </button>
            )}
            <Link to={backUrl} className="training-btn-primary" style={{ textAlign: "center", textDecoration: "none" }}>
              Back to {TRAINING.program.toLowerCase()}
            </Link>
          </div>
        </div>
        <TrainingFeedback feedback={feedback} />
      </Layout>
    );
  }

  return (
    <Layout
      title={activeLesson.title}
      titleChip="lesson"
      onBack={() => setActiveLesson(null)}
      breadcrumbs={moduleBreadcrumbs(activeLesson.title)}
    >
      <TrainingSteps steps={steps} current={currentStep} completed={stepsCompleted} />
      <div className="training-reading-label">
        {lessonTypeLabel(activeLesson.lesson_type)}
        {formatLessonDuration(activeLesson.duration_sec) && (
          <>
            {" · "}
            <span className="training-lesson-duration-inline">
              ~{formatLessonDuration(activeLesson.duration_sec)} expected
            </span>
          </>
        )}
      </div>

      {activeLesson.status !== "completed" && (
        <TrainingProgressBar
          value={engagementPct}
          label={
            activeLesson.lesson_type === "video"
              ? `Video watched: ${engagementPct}% (need ${minWatchPct}% to complete)`
              : `Reading progress: ${engagementPct}% scrolled`
          }
        />
      )}

      {activeLesson.lesson_type === "video" && activeLesson.media_url ? (
        <>
          <div className="training-video-wrap">
            <video
              ref={videoRef}
              src={activeLesson.media_url}
              controls
              playsInline
              preload="metadata"
            />
          </div>
          <p className="training-video-tip">Watch the full video, then mark the lesson complete.</p>
          {activeLesson.content_text ? (
            <div className="training-reading-card">
              <AcademyLessonContent
                key={activeLesson.id}
                markdown={activeLesson.content_text}
              />
            </div>
          ) : null}
        </>
      ) : (
        <div className="training-reading-card">
          {activeLesson.lesson_type === "image" && activeLesson.media_url ? (
            <img
              src={activeLesson.media_url}
              alt={activeLesson.title}
              className="training-lesson-hero-img"
              loading="lazy"
            />
          ) : null}
          <AcademyLessonContent
            key={activeLesson.id}
            markdown={activeLesson.content_text || ""}
          />
        </div>
      )}

      <TrainingStickyActions>
        <div className="training-sticky-inner training-lesson-nav">
          <button
            type="button"
            className="training-btn-secondary"
            onClick={() => setActiveLesson(null)}
          >
            Back
          </button>
          {activeLesson.status === "completed" ? (
            <button
              type="button"
              className="training-btn-primary"
              onClick={() => {
                const idx = lessons.findIndex((l) => l.id === activeLesson.id);
                const next = lessons[idx + 1];
                if (next) openLesson(next);
                else setActiveLesson(null);
              }}
            >
              {lessons.findIndex((l) => l.id === activeLesson.id) < lessons.length - 1
                ? "Next lesson"
                : "Back to module"}
            </button>
          ) : (
            <button
              type="button"
              className="training-btn-primary"
              disabled={
                completing ||
                engagementPct < minWatchPct
              }
              onClick={completeLesson}
            >
              {completing
                ? "Saving..."
                : engagementPct < minWatchPct
                  ? `Engage ${minWatchPct}% to continue`
                  : "Mark complete & continue"}
            </button>
          )}
        </div>
      </TrainingStickyActions>
      <TrainingFeedback feedback={feedback} />
    </Layout>
  );
}
